import { ToggleGroup } from 'radix-ui';
import { Box, Flex } from '@radix-ui/themes';
import { Badge, type BadgeProps } from './Badge';

type PillToggleBaseProps = {
  /** Badge color applied to every pill. */
  color?: BadgeProps['color'];
  /** Badge variant when an option is selected. */
  activeVariant?: BadgeProps['variant'];
  /** Badge variant when an option is not selected. */
  inactiveVariant?: BadgeProps['variant'];
  options: string[];
  disabledOptions?: string[];
  className?: string;
  /** Optional prefix for per-item `data-testid` attributes (`<prefix>-<option>`). */
  dataTestId?: string;
};

type PillToggleMultipleProps = PillToggleBaseProps & {
  type: 'multiple';
  value: string[];
  onValueChange: (value: string[]) => void;
};

type PillToggleSingleProps = PillToggleBaseProps & {
  type: 'single';
  value: string;
  onValueChange: (value: string) => void;
};

type PillToggleProps = PillToggleMultipleProps | PillToggleSingleProps;

/** Radix ToggleGroup — multi/single-select pills (onboarding, filters, sheets). */
const PillToggle = (props: PillToggleProps) => {
  const {
    color,
    activeVariant = 'solid',
    inactiveVariant = 'outline',
    options,
    disabledOptions = [],
    dataTestId,
    ...rootProps
  } = props;

  const isActive = (option: string) =>
    rootProps.type === 'multiple' ? rootProps.value.includes(option) : rootProps.value === option;

  const items = options.map(option => {
    const active = isActive(option);
    const disabled = disabledOptions.includes(option);
    return (
      <ToggleGroup.Item
        key={option}
        value={option}
        disabled={disabled}
        data-testid={dataTestId ? `${dataTestId}-${option}` : undefined}
        className="cursor-pointer data-disabled:cursor-not-allowed data-disabled:opacity-50"
      >
        <Badge color={color} variant={active ? activeVariant : inactiveVariant}>
          <Box py="1" px="1">
            {option}
          </Box>
        </Badge>
      </ToggleGroup.Item>
    );
  });

  return (
    <ToggleGroup.Root {...rootProps} rovingFocus={false}>
      <Flex wrap="wrap" gap="2">
        {items}
      </Flex>
    </ToggleGroup.Root>
  );
};

PillToggle.displayName = 'PillToggle';

export { PillToggle };
export type { PillToggleProps };
